import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { Geo } from './entities';
import { GeoRestful } from './restful';
import { GeoStore } from './store';

interface ErrorEvent {
  error: SafeError;
  errorType: 'get_geo_location_error' | 'get_zip_code_error';
  type: 'error';
}

interface GetGeoLocationEvent {
  geoLocation: Geo;
  type: 'get_geo_location';
}

interface GetZipCodeEvent {
  zipCode: string;
  type: 'get_zip_code';
}

export type GeoRestfulEvent = ErrorEvent | GetGeoLocationEvent | GetZipCodeEvent;

interface GeoFunctions {
  getGeoLocation: GeoRequest['getGeoLocation'];
  getZipCode: GeoRequest['getZipCode'];
}

export class GeoRequest extends ExtendedSubscribable<GeoRestfulEvent, GeoFunctions> {
  private restful: GeoRestful;
  private store: GeoStore;

  constructor(session: Session) {
    super();
    this.restful = new GeoRestful(session);
    this.store = new GeoStore();
  }

  public getGeoLocation = async (): SafePromise<Geo> => {
    if (!this.store.shouldFetchGeoLocation()) {
      const cachedGeoLocation = this.store.getGeoLocation();
      if (cachedGeoLocation) {
        this.dispatch({
          geoLocation: cachedGeoLocation,
          type: 'get_geo_location',
        });
        return { ok: cachedGeoLocation };
      }
    }

    const response = await this.restful.getGeoLocation();

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_geo_location_error',
        type: 'error',
      });
    } else {
      this.store.setGeoLocation(response.ok);

      this.dispatch({
        geoLocation: response.ok,
        type: 'get_geo_location',
      });
    }
    return response;
  };

  public getZipCode = async (city: string, region: string): SafePromise<string> => {
    const response = await this.restful.getZipCode(city, region);

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_zip_code_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'get_zip_code',
        zipCode: response.ok[0],
      });
    }
    return { ok: response.ok[0] };
  };

  public onSubscribe = (): GeoFunctions => ({
    getGeoLocation: this.getGeoLocation,
    getZipCode: this.getZipCode,
  });
}
